import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { TrackRecordSection } from '@shared/ui/home/TrackRecordSection';
import SignupHero from './landing/SignupHero';
import { CupWeekHero, cupWeekHeroOn } from './CupWeekHero';
import EmailCaptureForm from './EmailCaptureForm';
import LiveIntelligence from './landing/LiveIntelligence';
import RecentWinners, { type WinnerRace } from './landing/RecentWinners';
import PreviewInvite from './landing/PreviewInvite';
import { GET_TOP_PICK_WINNERS_DETAILS } from '@shared/graphql/queries/marketingTileDetails';
import { GET_CONSENSUS_PICK_STATS } from '@shared/graphql/queries/consensusPickStats';

interface LandingHeroProps {
  promotionData?: any;
  promotionLoading?: boolean;
}

const LandingHero: React.FC<LandingHeroProps> = ({ promotionData, promotionLoading }) => {
  const { data: winnersData, loading: winnersLoading } = useQuery(GET_TOP_PICK_WINNERS_DETAILS, {
    variables: { analysisTypeId: 1, timePeriod: '1_MONTH', groupBy: 'time', page: 1, pageSize: 6 },
    errorPolicy: 'all',
  });
  const winners: WinnerRace[] = winnersData?.topPickWinnersDetails?.races ?? [];
  // show off a Prime Pick winner if there is one, else the latest winner
  const featured = winners.find((r) => r.isConsensus && r.topPickWinner) ?? winners.find((r) => r.topPickWinner);

  const { data: consensusData, loading: strikeLoading } = useQuery(GET_CONSENSUS_PICK_STATS, {
    variables: { timePeriod: '90_DAYS' },
    errorPolicy: 'all',
  });
  const total = consensusData?.consensusPickStats?.rows?.find((r: { trackType?: string }) => r.trackType === 'total');
  const strike = total ? { strikePct: total.agreeStrikePct, m1StrikePct: total.m1StrikePct } : undefined;

  return (
    <>
    {cupWeekHeroOn() && <CupWeekHero placement="landing" />}
    <div className="bg-white rounded-lg shadow-card mb-8 overflow-hidden">

      {/* Promo Banner */}
      {promotionData?.getActivePromotion && !promotionLoading && (
        <div className="bg-race-gold px-6 py-3 text-center">
          <span className="text-deep-navy font-bold">{promotionData.getActivePromotion.title}</span>
          <span className="ml-3 text-sm text-deep-navy/70">No credit card required.</span>
        </div>
      )}

      {/* Hero */}
      <SignupHero />

      {/* Live Racing Intelligence */}
      <div className="px-6 py-12 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col gap-4">
            <p className="text-racing-green text-sm font-semibold uppercase tracking-wider">Transparent performance</p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-deep-navy leading-tight">See how our tips are going before you sign up.</h2>
            <p className="text-gray-500 text-lg">Every result is tracked. Prime Picks are the races where our analysis is most sure of the winner.</p>
            <a href="#results" className="self-start border border-racing-green text-racing-green px-6 py-3 rounded-md font-bold hover:bg-racing-green/5 transition-colors">
              View recent results
            </a>
          </div>
          <LiveIntelligence featured={featured} strike={strike} strikeLoading={strikeLoading} />
        </div>
      </div>

      {/* Free preview: look inside before signing up */}
      <PreviewInvite />

      {/* Email Capture */}
      <div className="px-6 py-8 md:px-12 bg-deep-navy">
        <div className="max-w-5xl mx-auto">
          <EmailCaptureForm />
        </div>
      </div>

      {/* Recent winners */}
      <RecentWinners races={winners} loading={winnersLoading} />

      {/* Track Record */}
      <div id="winning-record" className="px-4 py-8 bg-white">
        {/* the same track record tiles members see on the home page */}
        <div className="max-w-5xl mx-auto">
          <TrackRecordSection analysisTypeId={1} />
        </div>
      </div>

      {/* What You Get */}
      <div id="features" className="px-4 py-10 bg-deep-navy">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-heading font-bold text-white mb-1 text-center">What You Get</h2>
          <p className="text-center text-gray-400 text-sm mb-8">Everything you need to back smarter, not harder</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/5 border border-racing-green/25 rounded-lg p-5">
              <div className="bg-racing-green w-10 h-10 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-heading font-bold mb-2 text-white">Data-Driven Selections</h3>
              <p className="text-gray-400 text-sm">Every tip is backed by form, speed ratings, track bias, and market data — not gut feel.</p>
            </div>

            <div className="bg-white/5 border border-racing-green/25 rounded-lg p-5">
              <div className="bg-racing-green w-10 h-10 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-heading font-bold mb-2 text-white">Top 4 for Every Race</h3>
              <p className="text-gray-400 text-sm">Our Top 4 selections contain the winner in roughly 70% of races. More coverage, more confidence.</p>
            </div>

            <div className="bg-white/5 border border-racing-green/25 rounded-lg p-5">
              <div className="bg-racing-green w-10 h-10 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-heading font-bold mb-2 text-white">Real-Time Updates</h3>
              <p className="text-gray-400 text-sm">Tips and results update live throughout the day. Always have the latest picture before you back.</p>
            </div>

            <div className="bg-white/5 border border-racing-green/25 rounded-lg p-5">
              <div className="bg-racing-green w-10 h-10 rounded-lg flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="font-heading font-bold mb-2 text-white">Performance Tracking</h3>
              <p className="text-gray-400 text-sm">Full transparency on strike rates, ROI, and historical results. See exactly how the analysis performs.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div id="how-it-works" className="px-4 py-8 bg-white border-t border-racing-gray-darker">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-heading font-bold text-deep-navy mb-1 text-center">How It Works</h2>
          <p className="text-center text-gray-500 text-sm mb-8">Up and running in under a minute</p>

          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-4 bg-racing-gray p-4 rounded-lg">
              <div className="bg-racing-green text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <h3 className="font-heading font-bold text-deep-navy mb-1">Create your free account</h3>
                <p className="text-gray-500 text-sm">Sign up in under a minute — no credit card, just your email and mobile number.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-racing-gray p-4 rounded-lg">
              <div className="bg-racing-green text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <h3 className="font-heading font-bold text-deep-navy mb-1">Browse today's tips</h3>
                <p className="text-gray-500 text-sm">Instant access to Top 4 selections, form data, and confidence ratings for every race today.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-racing-gray p-4 rounded-lg">
              <div className="bg-racing-green text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <h3 className="font-heading font-bold text-deep-navy mb-1">Back smarter, track your results</h3>
                <p className="text-gray-500 text-sm">Use the data to make informed decisions, then watch your performance improve over time.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="bg-racing-green text-white px-8 py-3 rounded-md font-bold hover:bg-racing-green-dark transition-colors text-center"
            >
              Create Your Free Account
            </Link>
            <Link
              to="/preview"
              className="border border-racing-green text-racing-green px-8 py-3 rounded-md font-bold hover:bg-racing-green/5 transition-colors text-center"
            >
              See the Preview First
            </Link>
          </div>

          <p className="text-gray-400 text-xs text-center mt-4">
            TippingIQ provides racing analysis for informational purposes. Any wagering decisions are your own and must be placed through a licensed operator. Please gamble responsibly.
          </p>
        </div>
      </div>

    </div>
    </>
  );
};

export default LandingHero;